import { Button, Center, Flex, Input, Spinner, Text, Textarea, useToast } from '@chakra-ui/react';
import { useContext, useEffect, useState } from 'react';
import { BrowserView } from 'react-device-detect';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { AuthenticatedComponent } from '../auth/keycloak';
import { BrowserSidebar } from '../component/globalComponents/BrowserSidebar';
import { BrowserTopBar } from '../component/globalComponents/BrowserTopBar';
import { MobileViewPage } from '../component/globalComponents/MobileViewPage';
import { UploadImageComponent } from '../component/setupComponents/UploadImageComponent';
import PageDataContext from '../context/PageDataContext';
import { usePageDataCache } from '../hooks/usePageDataCache';
import {
    deleteAvatar,
    getUserPrivateProfile,
    setUserPrivateProfile,
    uploadAvatar,
    UserProfile,
} from '../service/social';
import { getFileFromURL } from '../util/util';

export const EditProfile: AuthenticatedComponent = ({ keycloak }) => {
    const { isLoadFromCache } = useContext(PageDataContext);
    const navigate = useNavigate();
    const toast = useToast();
    const [isLoading, setIsLoading] = useState(true);

    const userID = keycloak.subject;
    const userToken = keycloak.token;

    const usernameLimit = 36;
    const blurbLimit = 255;

    const handleUsername = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value.replace(/\s/g, '');
        setUsername(inputValue);
    };

    const localStoragePrefix = `ManageProfile`;

    const [userProfile, setUserProfile] = usePageDataCache<UserProfile | undefined>(
        `${localStoragePrefix}_userProfile`,
        undefined,
    );

    const [username, setUsername] = usePageDataCache(`${localStoragePrefix}_username`, '');
    const [validUsername, setValidUsername] = usePageDataCache(
        `${localStoragePrefix}_validUsername`,
        false,
    );

    const [blurb, setBlurb] = usePageDataCache(`${localStoragePrefix}_blurb`, '');
    const [validBlurb, setValidBlurb] = usePageDataCache(`${localStoragePrefix}_validBlurb`, false);

    const [profilePictureURL, setProfilePictureURL] = usePageDataCache(
        `${localStoragePrefix}_profilePictureURL`,
        '',
    );

    const intl = useIntl();

    const handleBlurb = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setBlurb(e.target.value);
    };

    const saveButton = async () => {
        const profile = await getUserPrivateProfile(keycloak.subject, keycloak.token);
        if (!profile) {
            toast({
                title: intl.formatMessage({
                    id: 'global.error_occurred',
                    defaultMessage: 'An error occurred.',
                }),
                description: intl.formatMessage({
                    id: 'global.try_again',
                    defaultMessage: 'Please try again.',
                }),
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
            return;
        }

        if (!!profilePictureURL && profilePictureURL !== profile.avatar_object_url) {
            const file = await getFileFromURL(profilePictureURL);
            if (!!file) {
                const success = await uploadAvatar(keycloak.subject, keycloak.token, file);
                if (!success) {
                    toast({
                        title: intl.formatMessage({
                            id: 'global.error_occurred',
                            defaultMessage: 'An error occurred.',
                        }),
                        description: intl.formatMessage({
                            id: 'setup_profile.upload_profile_picture_failed_description',
                            defaultMessage: 'Failed to upload profile picture. Please try again.',
                        }),
                        status: 'error',
                        duration: 9000,
                        isClosable: true,
                    });
                    return;
                }
            }
        } else if (!profilePictureURL && !!profile.avatar_object_url) {
            const success = deleteAvatar(keycloak.subject, keycloak.token);
            if (!success) {
                toast({
                    title: intl.formatMessage({
                        id: 'global.error_occurred',
                        defaultMessage: 'An error occurred.',
                    }),
                    description: intl.formatMessage({
                        id: 'global.delete_profile_picture_failed_description',
                        defaultMessage: 'Failed to delete previous avatar. Please try again.',
                    }),
                    status: 'error',
                    duration: 9000,
                    isClosable: true,
                });
                return;
            }
        }

        if (!!username) {
            profile.username = username;
        }

        if (!!blurb || blurb === '') {
            profile.description = blurb;
        }

        const resp = await setUserPrivateProfile(keycloak.subject, keycloak.token, profile);
        if (!resp.ok) {
            toast({
                title: intl.formatMessage({
                    id: 'global.error_occurred',
                    defaultMessage: 'An error occurred.',
                }),
                description: intl.formatMessage({
                    id: 'global.try_again',
                    defaultMessage: 'Please try again.',
                }),
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
            return;
        }
        navigate('/profile', { replace: true });
    };

    useEffect(() => {
        if (!isLoadFromCache) {
            if (username.length < 3 || username.length > usernameLimit) {
                setValidUsername(false);
            } else {
                setValidUsername(true);
            }

            if (blurb.length > blurbLimit) {
                setValidBlurb(false);
            } else {
                setValidBlurb(true);
            }
        }
    }, [username, blurb]);

    useEffect(() => {
        const setup = async () => {
            const profile = await getUserPrivateProfile(userID, userToken);
            setUserProfile(profile);
            setUsername(profile.username);
            setBlurb(profile.description);
            setProfilePictureURL(profile.avatar_object_url);
            setIsLoading(false);
        };

        if (!isLoadFromCache) {
            setup();
        } else {
            setIsLoading(false);
        }
    }, []);

    return (
        <>
            {isLoading && userProfile ? (
                <Center width="100%" height="30vh">
                    <Spinner data-cy="loading" />
                </Center>
            ) : (
                <>
                    <BrowserView>
                        <BrowserTopBar isLoading={isLoading} loadingColor={'#6ef1d5'} />
                        <BrowserSidebar />
                        <Flex className={'browser-content'}>
                            <Flex className={'edit-profile-browser'}>
                                <Text className={'edit-profile-title'}>EDIT PROFILE</Text>
                                <Flex className={'divider'} />
                                <UploadImageComponent
                                    id="edit-profile-upload-profile-picture"
                                    className="edit-profile-picture-container"
                                    onChange={setProfilePictureURL}
                                    footerText={[
                                        `${intl.formatMessage({
                                            id: 'global.add_profile_pic',
                                            defaultMessage: 'Add Profile Pic',
                                        })}
                                        - 130 PX * 130 PX`,
                                    ]}
                                    imageURL={profilePictureURL}
                                />
                                <Flex className={'divider'} />
                                <Flex>
                                    <Text>Name: </Text>
                                    <Input value={username} onChange={handleUsername} />
                                </Flex>
                                {validUsername ? (
                                    <Text className={'validation-text'}>
                                        {username.length}/{usernameLimit}
                                    </Text>
                                ) : (
                                    <Text className={'validation-text-error'}>
                                        Must be between 3 and {usernameLimit} charcters.{' '}
                                        {username.length}/{usernameLimit}
                                    </Text>
                                )}
                                <Flex className={'divider'} />
                                <Flex>
                                    <Text>About: </Text>
                                    <Textarea value={blurb} onChange={handleBlurb} />
                                </Flex>
                                {validBlurb ? (
                                    <Text className={'validation-text'}>
                                        {blurb.length}/{blurbLimit}
                                    </Text>
                                ) : (
                                    <Text className={'validation-text-error'}>
                                        Must be {blurbLimit} charcters or less. {blurb.length}/
                                        {blurbLimit}
                                    </Text>
                                )}
                                <Flex className={'edit-profile-menu'}>
                                    <Button
                                        onClick={saveButton}
                                        isDisabled={!validBlurb || !validUsername}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            navigate('/profile', { replace: true });
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Flex>
                            </Flex>
                        </Flex>
                    </BrowserView>
                    <MobileViewPage
                        title=""
                        titleColor=""
                        titleBackgroundColor="#6ef1d5"
                        customBanner={<></>}
                        isLoading={isLoading}
                    >
                        <Flex className={'edit-profile'}>
                            <Flex className={'edit-profile-menu'}>
                                <Button
                                    onClick={() => {
                                        navigate('/profile', { replace: true });
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Text className={'edit-profile-title'}>Edit Profile</Text>
                                <Button
                                    onClick={saveButton}
                                    isDisabled={!validBlurb || !validUsername}
                                >
                                    Save
                                </Button>
                            </Flex>
                            <Flex className={'divider'} />
                            <UploadImageComponent
                                id="edit-profile-upload-profile-picture"
                                className="edit-profile-picture-container"
                                onChange={setProfilePictureURL}
                                headerText={[
                                    intl.formatMessage({
                                        id: 'global.add_profile_pic',
                                        defaultMessage: 'Add Profile Pic',
                                    }),
                                ]}
                                footerText={['130 PX * 130 PX']}
                                imageURL={profilePictureURL}
                            />
                            <Flex className={'divider'} />
                            <Flex>
                                <Text>Name: </Text>
                                <Input value={username} onChange={handleUsername} />
                            </Flex>
                            {validUsername ? (
                                <Text className={'validation-text'}>
                                    {username.length}/{usernameLimit}
                                </Text>
                            ) : (
                                <Text className={'validation-text-error'}>
                                    Must be between 3 and {usernameLimit} charcters.{' '}
                                    {username.length}/{usernameLimit}
                                </Text>
                            )}
                            <Flex className={'divider'} />
                            <Flex>
                                <Text>About: </Text>
                                <Textarea
                                    className={'blurb-textarea'}
                                    value={blurb}
                                    onChange={handleBlurb}
                                />
                            </Flex>
                            {validBlurb ? (
                                <Text className={'validation-text'}>
                                    {blurb.length}/{blurbLimit}
                                </Text>
                            ) : (
                                <Text className={'validation-text-error'}>
                                    Must be {blurbLimit} charcters or less. {blurb.length}/
                                    {blurbLimit}
                                </Text>
                            )}
                        </Flex>
                    </MobileViewPage>
                </>
            )}
        </>
    );
};
